<template>
  <div class="list" :class="{ 'list--top': !isAtTop, 'list--bottom': !isAtBottom }">
    <ul class="list__box" ref="scrollContainer" @scroll="handleScroll">
      <li
        class="list__item"
        :class="{ 
          'list__item--transparent': dragIndex === index && ghost,
          'list__item--active-editing': isEditing && hasPointer,
          'list__item--only-delete': isEditing && !hasPointer
        }"
        v-for="({ coin }, index) in assets"
        :key="coin.api_id"
      >
        <div class="list__burger" @pointerdown="(e) => startDrag(e, index)" v-if="isEditing && hasPointer">
          <BurgerIcon />
        </div>
        <CoinItem class="list__item-coin" :image="coin.image" :name="coin.name" :ticker="coin.symbol" :href="coin.dropstab_url"/>
        <span class="list__item-price">${{ coin.price_usd }}</span>
        <span class="list__item-percent" :class="coin.change_1d_usd < 0 ? 'list__item-percent--red' : 'list__item-percent--green'">
          {{ parseFloat(coin.change_1d_usd)?.toFixed(2) }}%
        </span>
        <BusketIcon class="list__remove" @click="toggleAsset(coin)" v-if="isEditing" />
      </li>
    </ul>
  </div>
</template>

<script setup>
  import { ref, onMounted, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import { useBriefcaseState } from '@/Groups/Briefcase/private/composables/computedValues';
  //------------------------------
  import BusketIcon from '@/Groups/Briefcase/private/svg/BusketIcon.vue';
  import BurgerIcon from '@/Groups/Briefcase/private/svg/BurgerIcon.vue';
  import CoinItem from '@/Groups/Briefcase/private/ui/CoinItem/CoinItem.vue';
  //------------------------------
  const store = useStore();
  //------------------------------
  const { assets, isEditing } = useBriefcaseState();
  //------------------------------
  const scrollContainer = ref(null);
  const isAtTop = ref(true);
  const isAtBottom = ref(false);
  const dragging = ref(null);
  const dragIndex = ref(null);
  const ghost = ref(null);
  const hasPointer = ref(true);
  //------------------------------
  onMounted(() => {
    nextTick(() => {
      if (scrollContainer.value) {
        handleScroll({ target: scrollContainer.value });
      }
    });
    if (window.matchMedia('(hover: none)').matches && window.matchMedia('(pointer: coarse)').matches) {
      hasPointer.value = false;
    }
  });
  //------------------------------
  //Проверяет, находится ли список вверху или внизу при прокрутке
  const handleScroll = (e) => {
    if (!e.target) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const tolerance = 1;
    isAtTop.value = scrollTop <= tolerance;
    isAtBottom.value = scrollHeight - (scrollTop + clientHeight) <= tolerance;
  };
  //Добавляет или удаляет актив в портфеле (диспатчит Vuex-экшен)
  const toggleAsset = (asset) => {
    store.dispatch('Briefcase/toggleAsset', asset);
  };
  //Инициализирует перетаскивание: создает "призрак", навешивает события
  const startDrag = (e, index) => {
    e.preventDefault();
    const item = e.target.closest('.list__item');
    if (!item) return;
    dragging.value = item;
    dragIndex.value = index;
    const rect = item.getBoundingClientRect();
    const clone = item.cloneNode(true);
    ghost.value = clone;
    clone.classList.add('list__drag-ghost');
    clone.style.position = 'fixed';
    clone.style.top = `${e.pageY - rect.height / 2}px`;
    clone.style.left = rect.left + 'px';
    clone.style.width = rect.width + 'px';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = 99999;
    document.body.appendChild(clone);
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
  };
  //Определяет индекс ближайшего элемента к позиции курсора
  const getClosestIndex = (clientY) => {
    const listItems = Array.from(scrollContainer.value.querySelectorAll('.list__item'));
    let closestIndex = 0;
    let closestDistance = Infinity;
    listItems.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      const middleY = rect.top + rect.height / 2;
      const distance = Math.abs(clientY - middleY);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });
    return closestIndex;
  };
  //Обновляет позицию "призрака" и сдвигает остальные элементы во время перетаскивания
  const onDragMove = (e) => {
    if (!ghost.value) return;
    ghost.value.style.top = `${e.clientY - ghost.value.offsetHeight / 2}px`;
    const targetIndex = getClosestIndex(e.clientY);
    const listItems = Array.from(scrollContainer.value.querySelectorAll('.list__item'));
    listItems.forEach((el, i) => {
      el.style.transition = 'transform 0.2s ease';
      if (i === dragIndex.value) {
        el.style.transform = '';
        return;
      }
      if (dragIndex.value < targetIndex && i > dragIndex.value && i <= targetIndex) {
        el.style.transform = 'translateY(-100%)';
      } else if (dragIndex.value > targetIndex && i < dragIndex.value && i >= targetIndex) {
        el.style.transform = 'translateY(100%)';
      } else {
        el.style.transform = '';
      }
    });
    autoScroll(e.clientY);
  };
  //Завершает перетаскивание: обновляет порядок, удаляет "призрак", чистит события
  const onDragEnd = (e) => {
    const newIndex = getClosestIndex(e.clientY);
    const listItems = scrollContainer.value.querySelectorAll('.list__item');
    listItems.forEach((el) => {
      el.style.transform = '';
      el.style.transition = '';
    });
    if (newIndex !== dragIndex.value) {
      store.dispatch('Briefcase/moveAsset', {
        from: dragIndex.value,
        to: newIndex,
      });
    }
    dragging.value = null;
    dragIndex.value = null;
    if (ghost.value) {
      ghost.value.remove();
      ghost.value = null;
    }
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
  };
  // Автоматически прокручивает список при приближении курсора к краям
  const autoScroll = (y) => {
    const container = scrollContainer.value;
    if (!container) return;

    const edgeThreshold = 40;
    const scrollSpeed = 5;

    const containerRect = container.getBoundingClientRect();

    if (y - containerRect.top < edgeThreshold) {
      container.scrollTop -= scrollSpeed;
    } else if (containerRect.bottom - y < edgeThreshold) {
      container.scrollTop += scrollSpeed;
    }
  };
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>